import React, { createContext, useContext, useState, useCallback } from "react";
import de from "./de.json";
import en from "./en.json";

const DICTS = { de, en };
const I18nContext = createContext(null);

function resolve(dict, path) {
  return path.split(".").reduce((acc, k) => (acc && acc[k] != null ? acc[k] : null), dict);
}

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(() => localStorage.getItem("mayr_locale") || "de");

  const setLocale = useCallback((l) => {
    localStorage.setItem("mayr_locale", l);
    setLocaleState(l);
    document.documentElement.lang = l;
  }, []);

  const t = useCallback(
    (key, vars) => {
      let val = resolve(DICTS[locale], key);
      if (val == null) val = resolve(DICTS.en, key);
      if (val == null) return key;
      if (vars) {
        Object.entries(vars).forEach(([k, v]) => {
          val = val.replace(new RegExp(`\\{${k}\\}`, "g"), v);
        });
      }
      return val;
    },
    [locale]
  );

  const fmtCurrency = useCallback(
    (value, currency = "EUR") => {
      if (value == null || value === "") return "";
      return new Intl.NumberFormat(locale === "de" ? "de-DE" : "en-GB", {
        style: "currency",
        currency,
      }).format(Number(value));
    },
    [locale]
  );

  const fmtNumber = useCallback(
    (value, digits = 2) => {
      if (value == null || value === "") return "";
      return new Intl.NumberFormat(locale === "de" ? "de-DE" : "en-GB", {
        minimumFractionDigits: 0,
        maximumFractionDigits: digits,
      }).format(Number(value));
    },
    [locale]
  );

  const fmtDate = useCallback(
    (value) => {
      if (!value) return "";
      const dObj = new Date(value);
      if (isNaN(dObj)) return value;
      return new Intl.DateTimeFormat(locale === "de" ? "de-DE" : "en-GB", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(dObj);
    },
    [locale]
  );

  return (
    <I18nContext.Provider value={{ locale, setLocale, t, fmtCurrency, fmtNumber, fmtDate }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}

export const LOCALE_KEYS = { de: Object.keys(de), en: Object.keys(en) };
